import axios from 'axios'
import { z } from 'zod'
import { parseRequestError } from '@/common/utilities.ts'
import { RequestResponse } from '@/common/types_and_interfaces.ts'
import { LOG_TYPES } from '@/common/constants.ts'

type InviteUserReq = {
  first_name: string
  last_name: string
  email?: string
  phone?: string
  role_id: number[]
  invite_callback_url: string
  program_id?: number
  selectedUserId?: number | null
}

const InviteUserRespSchema = z.object({
  id: z.number(),
})

export type UserInviteResp = z.infer<typeof InviteUserRespSchema>

export const inviteUser = async (
  requestBody: InviteUserReq
): Promise<RequestResponse<UserInviteResp>> => {
  try {
    const response = await axios.post('users', requestBody)
    const cleanResponse = InviteUserRespSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export async function GETInvitationStatus(
  email: string,
  invitationToken: string
): Promise<RequestResponse<''>> {
  try {
    await axios.get(
      `/public/users/invitation_status?email=${email}&invitation_token=${invitationToken}`
    )
    return { data: '', error: null }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}

type ResendInvitationRequest = {
  email: string
  invitation_token: string
  call_back_url: string
}

export async function POSTResendInvitation(
  payload: ResendInvitationRequest
): Promise<RequestResponse<''>> {
  try {
    await axios.post(`/public/users/resend_email_invitation`, payload)
    return { data: '', error: null }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}

type RegisterUserReq = {
  id: number
  token: string
  payload: {
    email: string
    password: string
    phone: string
  }
}

const RegisterUserRespSchema = z.object({
  id: z.number(),
})

export type UserRegistrationResp = z.infer<typeof RegisterUserRespSchema>

export const registerUser = async (
  requestBody: RegisterUserReq
): Promise<RequestResponse<UserRegistrationResp>> => {
  try {
    const response = await axios.put(
      `users/${requestBody.id}/complete_registration`,
      requestBody.payload,
      {
        headers: {
          Authorization: `Bearer ${requestBody.token}`, // Set the Bearer token in the headers
        },
      }
    )
    const cleanResponse = RegisterUserRespSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

// For Reset Passwod Email

const ResetPasswordEmailSchema = z.object({
  message: z.string(),
})
export type ResetPasswordEmailResponse = z.infer<
  typeof ResetPasswordEmailSchema
>

export const SendResetPasswordEmail = async (
  email: string
): Promise<RequestResponse<ResetPasswordEmailResponse>> => {
  try {
    const response = await axios.post('users/send_reset_password_email', {
      email,
    })
    const cleanResponse = ResetPasswordEmailSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

// RESET PASSWORD

type ResetPasswordRequest = {
  token: string
  user_id: string
  payload: {
    password: string
  }
}

const ResetPasswordResponseSchema = z.object({
  message: z.string().nullable(),
})

type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>

export const ResetPassword = async (
  requestBody: ResetPasswordRequest
): Promise<RequestResponse<ResetPasswordResponse>> => {
  try {
    const response = await axios.put(
      `users/${requestBody.user_id}/reset_password`,
      requestBody.payload,
      {
        headers: {
          Authorization: `Bearer ${requestBody.token}`,
        },
      }
    )
    return {
      data: ResetPasswordResponseSchema.parse(response.data),
      error: null,
    }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}

// For Resend User Invitation Email

const ResendEmailInvitationSchema = z.object({
  message: z.string(),
})
export type ResendEmailInvitationResponse = z.infer<
  typeof ResendEmailInvitationSchema
>

export const ResendEmailInvitation = async (
  email: string,
  invite_callback_url?: string
): Promise<RequestResponse<ResendEmailInvitationResponse>> => {
  try {
    const response = await axios.post('users/resend_email_invitation', {
      email,
      invite_callback_url,
    })
    const cleanResponse = ResendEmailInvitationSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

// For Resend TWO FA

const ResendTwoFaSchema = z.object({
  message: z.string(),
})
export type ResendTWOFAResponse = z.infer<typeof ResendTwoFaSchema>

export const ResendTwoFA = async (
  phone: string
): Promise<RequestResponse<ResendTWOFAResponse>> => {
  try {
    const response = await axios.post('/auth/resend-2fa', {
      phone,
    })
    const cleanResponse = ResendTwoFaSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

// For Verify TWO FA Code

const AuthResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.string(),
  expires_in: z.number(),
  message: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
})

export type VerifyTwoFaResponse = z.infer<typeof AuthResponseSchema>

export const VerifyTwoFA = async (
  phone: string,
  code: string
): Promise<RequestResponse<VerifyTwoFaResponse>> => {
  try {
    const response = await axios.post('/auth/verify-2fa', {
      phone,
      code,
    })
    const cleanResponse = AuthResponseSchema.parse(response.data)
    return { data: cleanResponse, error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

export type LogUserActivityRequest = {
  log_type: (typeof LOG_TYPES)[keyof typeof LOG_TYPES]
  suffix_text: string
  targetUserName?: string
}

export async function LogUserActivity({
  log_type,
  suffix_text,
  targetUserName,
}: LogUserActivityRequest): Promise<RequestResponse<''>> {
  const endpoint = `/log_user_activity`
  const params = new URLSearchParams({
    log_type,
    suffix_text,
  })
  if (targetUserName) params.append('targetUserName', targetUserName)
  try {
    await axios.post(`${endpoint}?${params}`)
    return { data: '', error: null }
  } catch (error) {
    return { data: null, error: parseRequestError(error) }
  }
}

// Change PASSWORD

type ChangePasswordRequest = {
  user_id: number
  payload: {
    current_password: string
    new_password: string
    new_password_confirmation: string
  }
}

const ChangePasswordResponseSchema = z.object({
  id: z.number(),
})

type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>

export const ChangePassword = async (
  requestBody: ChangePasswordRequest
): Promise<RequestResponse<ChangePasswordResponse>> => {
  try {
    const response = await axios.put(
      `users/${requestBody.user_id}/change_password`,
      requestBody.payload
    )
    return {
      data: ChangePasswordResponseSchema.parse(response.data),
      error: null,
    }
  } catch (e) {
    return { data: null, error: parseRequestError(e) }
  }
}
